import { useMemo } from 'react';
import { Box, Typography, Divider, CircularProgress, Chip } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { rpcCall } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import { gwtStyles } from '../../config/theme';
import type { Study } from '../../api/types';

/** Raw response from getDocument(documentType=REPORT) */
interface ReportResult {
  content?: string | string[];
  documentContent?: string;
  documents?: string[];
}

/** Parsed report envelope from getExtendedReportText() */
interface ParsedReport {
  text: string;
  reportStatus?: string;
  authorString?: string;
  dateString?: string;
  format?: string;
  addendums?: ParsedReport[];
}

/** Status chip colour */
function statusColor(status?: string): 'success' | 'warning' | 'info' | 'default' | 'error' {
  switch (status?.toUpperCase()) {
    case 'SIGNED': case 'AMENDED': return 'success';
    case 'PRELIMINARY': return 'warning';
    case 'TRANSCRIBED': return 'info';
    case 'DRAFT': return 'default';
    default: return 'default';
  }
}

/**
 * Parse a raw report string from getExtendedReportText().
 * The backend returns JSON like:
 *   {"ver":3,"format":"rawtext","text":"...","reportStatus":"DRAFT",
 *    "authorString":"Dr Smith","dateString":"02/26/2026 10:21 AM EST",
 *    "addendums":[...]}
 * Or it may be plain HTML (legacy reports).
 */
function parseReportString(raw: string): ParsedReport {
  const trimmed = raw.trim();
  // Try JSON parse
  if (trimmed.startsWith('{')) {
    try {
      const obj = JSON.parse(trimmed);
      const addendums: ParsedReport[] = [];
      if (Array.isArray(obj.addendums)) {
        for (const a of obj.addendums) {
          addendums.push({
            text: a.text ?? '',
            reportStatus: a.reportStatus,
            authorString: a.authorString,
            dateString: a.dateString,
            format: a.format,
          });
        }
      }
      return {
        text: obj.text ?? '',
        reportStatus: obj.reportStatus,
        authorString: obj.authorString,
        dateString: obj.dateString,
        format: obj.format,
        addendums: addendums.length > 0 ? addendums : undefined,
      };
    } catch {
      // Not valid JSON — fallback to raw text
    }
  }
  // Plain text or HTML — use as-is
  return { text: trimmed };
}

/**
 * Fetch all reports for a study using getDocument RPC.
 */
function useStudyReports(study: Study | null) {
  return useQuery({
    queryKey: queryKeys.reports(study?.objKey ?? ''),
    queryFn: async (): Promise<ParsedReport[]> => {
      if (!study) return [];
      const result = await rpcCall<ReportResult>('getDocument', {
        documentType: 'REPORT',
        studyKey: study.objKey,
      });
      if (!result) return [];
      let rawList: string[] = [];
      if (result.content) {
        rawList = Array.isArray(result.content) ? result.content : [result.content];
      } else if (result.documents && result.documents.length > 0) {
        rawList = result.documents;
      } else if (result.documentContent) {
        rawList = [result.documentContent];
      }
      return rawList.map(parseReportString);
    },
    enabled: !!study,
    staleTime: 30_000,
  });
}

interface ReportViewerProps {
  study: Study | null;
}

/** Render a single report or addendum block */
const ReportBlock = ({
  report,
  label,
}: {
  report: ParsedReport;
  label: string;
}) => {
  const isHtml = report.text.trim().startsWith('<');
  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {/* Metadata line */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: 'text.secondary' }}>
          {label}
        </Typography>
        {report.reportStatus && (
          <Chip
            label={report.reportStatus}
            size="small"
            color={statusColor(report.reportStatus)}
            variant="outlined"
            sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
          />
        )}
        {report.authorString && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            by {report.authorString}
          </Typography>
        )}
        {report.dateString && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
            — {report.dateString}
          </Typography>
        )}
      </Box>
      {/* Report body */}
      {report.text ? (
        isHtml ? (
          <Box
            sx={{
              fontSize: 13,
              fontFamily: 'monospace',
              lineHeight: 1.6,
              '& table': { borderCollapse: 'collapse', fontSize: 12 },
              '& td, & th': { border: '1px solid', borderColor: 'divider', px: 0.5 },
            }}
            dangerouslySetInnerHTML={{ __html: report.text }}
          />
        ) : (
          <Typography
            variant="body2"
            component="pre"
            sx={{
              fontSize: 13,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
              m: 0,
              color: 'text.primary',
            }}
          >
            {report.text}
          </Typography>
        )
      ) : (
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }}>
          (empty report)
        </Typography>
      )}
      {/* Addendums */}
      {report.addendums?.map((addendum, aIdx) => (
        <Box key={aIdx} sx={{ ml: 2, mt: 1.5, pl: 1.5, borderLeft: '2px solid', borderColor: 'info.main' }}>
          <ReportBlock report={addendum} label={`Addendum ${aIdx + 1}`} />
        </Box>
      ))}
    </Box>
  );
};

/**
 * Report viewer panel — shows all reports/addendums for the selected study.
 * Mirrors GWT's report viewing behavior.
 */
export const ReportViewer = ({ study }: ReportViewerProps) => {
  const { data: reports, isLoading, error } = useStudyReports(study);

  const content = useMemo(() => {
    if (!study) {
      return (
        <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', fontSize: 12 }}>
          Select a study to view reports.
        </Typography>
      );
    }
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }
    if (error) {
      return (
        <Typography variant="body2" sx={{ p: 2, color: 'error.main', fontSize: 12 }}>
          Failed to load reports: {String(error)}
        </Typography>
      );
    }
    if (!reports || reports.length === 0) {
      return (
        <Typography variant="body2" sx={{ p: 2, color: 'text.secondary', fontSize: 12 }}>
          No reports available for this study.
        </Typography>
      );
    }
    return reports.map((report, idx) => (
      <Box key={idx}>
        {idx > 0 && <Divider sx={{ my: 0.5 }} />}
        <ReportBlock
          report={report}
          label={reports.length > 1 ? `Report ${idx + 1} of ${reports.length}` : 'Report'}
        />
      </Box>
    ));
  }, [study, reports, isLoading, error]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: gwtStyles.tabContent,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: 28,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          px: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: gwtStyles.mainView,
        }}
      >
        <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 600, color: gwtStyles.tabTitle }}>
          Reports
        </Typography>
        {study && (
          <Typography variant="caption" sx={{ ml: 1, fontSize: 11, color: 'text.secondary' }}>
            — {study.name} ({study.acc})
          </Typography>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {content}
      </Box>
    </Box>
  );
};
